import { STORAGE_LIMIT_BYTE } from '@/config/constants'
import { Box, Circle, Flex, HStack, Icon, Text } from '@chakra-ui/react'
import { useTranslation } from '@infraloop/i18n'
import { LuDatabase, LuFolder } from 'react-icons/lu'

import { bytesToHumanReadable } from '../utils'

/** Shared card chrome for the two stat tiles. */
const CARD_PROPS = {
  flex: '1',
  minW: '280px',
  bg: 'white',
  border: '1px solid',
  borderColor: 'gray.200',
  borderRadius: '14px',
  p: '18px 20px',
  boxShadow: '0 1px 3px rgba(16,24,40,.06)',
} as const

const StatRow = ({
  storageUsed,
  ownedCount,
  invitedCount,
}: {
  /** Storage used in bytes — the dashboard's owner (or the viewed user, for an admin). */
  storageUsed: number
  /** Number of projects the user owns. */
  ownedCount: number
  /** Number of projects the user was invited to. */
  invitedCount: number
}) => {
  // Hooks
  const { t } = useTranslation()

  // Derived state
  const storagePct = (storageUsed / STORAGE_LIMIT_BYTE) * 100
  const barWidth = Math.max(2, Math.min(100, storagePct))
  const projectCount = ownedCount + invitedCount

  return (
    <Flex gap="16px" flexWrap="wrap">
      <Box {...CARD_PROPS}>
        <HStack gap="11px" align="center">
          <Circle size="38px" borderRadius="10px" bg="primary.50" color="primary.600">
            <Icon as={LuDatabase} boxSize="19px" />
          </Circle>
          <Box flex="1">
            <Text fontSize="12px" color="gray.500" fontWeight="medium">
              {t('hatsuly.stats.storageUsed')}
            </Text>
            <Text fontSize="15px" color="gray.800" fontWeight="bold">
              {bytesToHumanReadable(storageUsed)}{' '}
              <Text as="span" color="gray.400" fontWeight="medium">
                / {bytesToHumanReadable(STORAGE_LIMIT_BYTE)}
              </Text>
            </Text>
          </Box>
          <Text fontSize="13px" fontWeight="semibold" color="primary.600">
            {storagePct.toFixed(1)}%
          </Text>
        </HStack>
        <Box mt="13px" h="7px" bg="gray.100" borderRadius="full" overflow="hidden">
          <Box h="100%" w={`${barWidth.toFixed(1)}%`} bg="primary.500" borderRadius="full" transition="width .2s" />
        </Box>
      </Box>

      <HStack {...CARD_PROPS} gap="11px" align="center">
        <Circle size="38px" borderRadius="10px" bg="green.50" color="green.600">
          <Icon as={LuFolder} boxSize="19px" />
        </Circle>
        <Box>
          <Text fontSize="12px" color="gray.500" fontWeight="medium">
            {t('hatsuly.stats.projectCount')}
          </Text>
          <HStack gap="8px" align="baseline" flexWrap="wrap">
            <Text fontSize="15px" color="gray.800" fontWeight="bold">
              {projectCount}{' '}
              <Text as="span" color="gray.400" fontWeight="medium">
                {t('hatsuly.stats.projectCountUnit')}
              </Text>
            </Text>
            <Text fontSize="12px" color="gray.400">
              {t('hatsuly.stats.projectBreakdown', { owned: ownedCount, invited: invitedCount })}
            </Text>
          </HStack>
        </Box>
      </HStack>
    </Flex>
  )
}

export default StatRow
